import React, { useState } from "react";
import { CashModalProps } from "../../utils/types";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import { User } from "lucide-react";

const CashModal: React.FC<CashModalProps> = ({ order, paymentMethod, receivedPrice, setReceivedPrice, handleCloseModal, handleConfirmPayment }) => {
  // ✅ Always declare hooks at the top
  const [errorText, setErrorText] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // ✅ Safe conditional return after all hooks
  if (!order) return null;

  const handleReceivedPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value) || 0;
    const remaining = Number(order?.RemainingAmount || 0);
    const newValue = Math.min(Math.max(0, val), remaining);
    setReceivedPrice(newValue);

    if (newValue !== remaining) {
      setErrorText("Full payment only");
    } else {
      setErrorText("");
    }
  };

  // Use cars array instead of productDetails
  const carNumbers = order.cars?.map((car) => car.carNumber).filter(Boolean) || [];
  const totalCars = carNumbers.length;
  const carNumbersText = carNumbers.join(", ") || "N/A";

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (totalCars > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left,
        y: rect.bottom + window.scrollY,
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <ModalWrapper title="Payment Summary" onClose={handleCloseModal} width="max-w-lg">
      <div className="space-y-5 text-sm text-gray-700">
        {/* Header Section */}
        <div className="bg-gray-50 border rounded-lg p-4 flex items-center gap-3">
          <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
            <User className="text-blue-600" size={24} />
          </div>

          <div className="flex flex-col">
            <div className="font-semibold text-gray-900">
              Customer: <span className="text-gray-800">{order.CustomerName || "N/A"}</span>
            </div>
            <div className="text-sm text-gray-600">
              Payment: <span className="capitalize">{paymentMethod}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Date: {order.Orderdate} | {order.Ordertime}
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Order Summary</h3>
          <hr className="border-gray-200 mb-2" />
          <div className="grid grid-cols-2 gap-y-1">
            <div>Order ID</div>
            <div className="text-right font-medium text-gray-900">{order.Orderid || "N/A"}</div>

            <div>Car Number</div>
            <div className="text-right relative cursor-help" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <span className="font-medium text-gray-900">
                {totalCars} car{totalCars !== 1 ? "s" : ""}
              </span>

              {/* Tooltip */}
              {showTooltip && totalCars > 0 && (
                <div
                  className="absolute z-50 px-3 py-2 text-xs bg-gray-900 text-white rounded-md shadow-lg max-w-xs break-words"
                  style={{
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginTop: "8px",
                  }}
                >
                  <div className="font-semibold mb-1">Car Numbers:</div>
                  <div>{carNumbersText}</div>
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              )}
            </div>

            <div>Order Amount</div>
            <div className="text-right">{order.totalOrderPrice || order.totalAmount || 0} QAR</div>

            <div>Previous Received Amount</div>
            <div className="text-right font-semibold text-gray-900">{order.payments?.[0]?.ReceivedAmount || 0} QAR</div>

            <div>Remaining Amount</div>
            <div className="text-right font-semibold text-gray-900">{order.RemainingAmount || 0} QAR</div>
          </div>
        </div>

        {/* Payment Status */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Payment Status</h3>
          <hr className="border-gray-200 mb-2" />
          <div className="grid grid-cols-2 gap-y-2">
            <div>Received Amount</div>
            <div className="text-right">
              <input
                type="number"
                value={receivedPrice || ""}
                onChange={handleReceivedPriceChange}
                className={`border rounded-md px-3 py-1.5 w-32 text-right focus:outline-none ${
                  errorText ? "border-red-500 focus:ring-2 focus:ring-red-400" : "border-gray-300 focus:ring-2 focus:ring-blue-500"
                }`}
              />
              {errorText && <div className="text-xs text-red-500 text-right mt-1">{errorText}</div>}
            </div>

            <div>Remaining Amount</div>
            <div className="text-right font-medium text-gray-900">{order.RemainingAmount || 0} QAR</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 border-t pt-4 mt-2">
          <button onClick={handleCloseModal} className="px-5 py-2 rounded-full border border-blue-500 text-blue-500 hover:bg-blue-50 font-medium transition">
            Cancel
          </button>
          <button
            onClick={() => handleConfirmPayment(paymentMethod)}
            disabled={Number(receivedPrice) !== Number(order.RemainingAmount) || receivedPrice <= 0}
            className={`px-5 py-2 rounded-full font-medium transition ${
              Number(receivedPrice) === Number(order.RemainingAmount) ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-blue-300 text-white cursor-not-allowed"
            }`}
          >
            Complete
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CashModal;